import { type FC, useId, useState } from "react";

import {
  InputRange,
  type InputRangeChangeSource,
} from "~/components/UI/FilterRange";

import { labelByEmbedDimension } from "../../constants";
import type { EmbedDimensionEnum } from "../../types";
import * as Styled from "./EmbedSizeField.styles";

interface IEmbedSizeFieldProps {
  dimension: EmbedDimensionEnum;
  value: number;
  min: number;
  max: number;
  errorMessageId?: string;
  onChange: (value: number) => void;
}

export const EmbedSizeField: FC<IEmbedSizeFieldProps> = ({
  dimension,
  value,
  min,
  max,
  errorMessageId,
  onChange,
}) => {
  const [draft, setDraft] = useState<number | null>();
  const inputId = useId();
  const shown = draft === undefined ? value : draft;
  const isOutOfRange = shown !== null && (shown < min || shown > max);

  const handleChange = (
    next: number | null,
    source: InputRangeChangeSource
  ) => {
    // The number field echoes a value the parent set; that is not typing.
    if (source === "prop") {
      setDraft(undefined);

      return;
    }

    if (next !== null && next >= min && next <= max) {
      setDraft(undefined);
      onChange(next);

      return;
    }

    setDraft(next);
  };

  // A size below the minimum is committed as typed — the preview then explains
  // what it needs. Above the maximum there is nothing to explain, so the value
  // settles on the largest size the widget is allowed to be. An emptied field
  // goes back to the last size.
  const commitDraft = () => {
    if (draft !== null && draft !== undefined) {
      onChange(Math.min(draft, max));
    }

    setDraft(undefined);
  };

  return (
    <Styled.Root $error={isOutOfRange}>
      <Styled.Label htmlFor={inputId}>
        {labelByEmbedDimension[dimension]}
      </Styled.Label>
      <InputRange
        inputId={inputId}
        value={shown}
        onChange={handleChange}
        onBlur={commitDraft}
        onKeyDown={({ key }) => key === "Enter" && commitDraft()}
        placeholder=""
        thousandSeparator={false}
        startAdornment={null}
        error={isOutOfRange}
        errorMessageId={isOutOfRange ? errorMessageId : undefined}
      />
    </Styled.Root>
  );
};
